/**
 * Dashboard Editor
 * Edit mode for dashboards: add/remove/reorder widgets, configure filters
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addWidget, deleteWidget, updateWidget, updateDashboardLayout } from './api';
import type { DashboardWidget, IoTDashboard } from './types';
import { createDefaultWidget, listWidgetTypes } from './widgetRegistry';
import AddWidgetModal from './AddWidgetModal';
import WidgetConfigurator from './WidgetConfigurator';

interface Props {
  dashboard: IoTDashboard;
  onExitEdit: () => void;
}

export default function DashboardEditor({ dashboard, onExitEdit }: Props) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null);
  const queryClient = useQueryClient();

  const addWidgetMutation = useMutation({
    mutationFn: (widget: DashboardWidget) => addWidget(dashboard.dashboardId, widget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iot', 'dashboards'] });
      setShowAddModal(false);
    }
  });

  const updateWidgetMutation = useMutation({
    mutationFn: ({ widgetId, updates }: { widgetId: string; updates: any }) => 
      updateWidget(dashboard.dashboardId, widgetId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iot', 'dashboards'] });
      setEditingWidget(null);
    }
  });

  const deleteWidgetMutation = useMutation({
    mutationFn: (widgetId: string) => deleteWidget(dashboard.dashboardId, widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iot', 'dashboards'] });
    }
  });

  const reorderMutation = useMutation({
    mutationFn: (widgets: DashboardWidget[]) => updateDashboardLayout(dashboard.dashboardId, widgets),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['iot', 'dashboards'] });
    }
  });

  const widgets = dashboard.layout?.widgets || [];

  const handleAddWidget = (type: string) => {
    const newWidget = createDefaultWidget(type, Date.now().toString());
    if (newWidget) {
      addWidgetMutation.mutate(newWidget);
    }
  };

  const handleDeleteWidget = (widgetId: string) => {
    if (confirm('Delete this widget?')) {
      deleteWidgetMutation.mutate(widgetId);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newWidgets = [...widgets];
    [newWidgets[index - 1], newWidgets[index]] = [newWidgets[index], newWidgets[index - 1]];
    reorderMutation.mutate(newWidgets);
  };

  const handleMoveDown = (index: number) => {
    if (index === widgets.length - 1) return;
    const newWidgets = [...widgets];
    [newWidgets[index], newWidgets[index + 1]] = [newWidgets[index + 1], newWidgets[index]];
    reorderMutation.mutate(newWidgets);
  };

  const handleSaveWidgetConfig = (widgetId: string, updates: Partial<DashboardWidget>) => {
    updateWidgetMutation.mutate({ widgetId, updates });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-blue-900">Edit Mode: {dashboard.name}</h2>
          <p className="text-sm text-blue-700 mt-1">Add, remove, or reorder widgets</p>
        </div>
        <button
          onClick={onExitEdit}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Done Editing
        </button>
      </div>

      {/* Add Widget Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          + Add Widget
        </button>
      </div>

      {/* Widgets List */}
      {widgets.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-600">No widgets yet. Click "Add Widget" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {widgets.map((widget, index) => (
            <div key={widget.id} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {widget.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">ID: {widget.id}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === widgets.length - 1}
                    className="px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => setEditingWidget(widget)}
                    className="px-3 py-1 text-sm text-blue-700 hover:bg-blue-50 rounded"
                  >
                    Configure
                  </button>
                  <button
                    onClick={() => handleDeleteWidget(widget.id)}
                    disabled={deleteWidgetMutation.isPending}
                    className="px-3 py-1 text-sm text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Show current config summary */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {widget.config && Object.keys(widget.config).length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Config:</span>{' '}
                      <span className="text-gray-600">{JSON.stringify(widget.config)}</span>
                    </div>
                  )}
                  {widget.filters && Object.keys(widget.filters).length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Filters:</span>{' '}
                      <span className="text-gray-600">{JSON.stringify(widget.filters)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Widget Modal */}
      {showAddModal && (
        <AddWidgetModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddWidget}
          isPending={addWidgetMutation.isPending}
        />
      )}

      {/* Widget Configurator Modal */}
      {editingWidget && (
        <WidgetConfigurator
          widget={editingWidget}
          onClose={() => setEditingWidget(null)}
          onSave={(updates) => handleSaveWidgetConfig(editingWidget.id, updates)}
          isPending={updateWidgetMutation.isPending}
        />
      )}
    </div>
  );
}
